import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import {
  ArrowLeftIcon,
  UsersIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { stripHtml } from '../../utils/sanitize';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import {
  DEFAULT_GROUP_WORK_LOCATION,
  type GroupWorkLocation,
} from '../../constants/groupWorkLocation';
import GroupWorkLocationField from './GroupWorkLocationField';

interface Group {
  id: string;
  name: string;
  description?: string;
  type: string;
  groupType: string;
  email?: string;
  isActive: boolean;
  managerId?: string;
  timezone: string;
  escalationLevel: number;
  autoAssign: boolean;
  skills: string[];
  workSchedule?: string;
  defaultHourlyRate: number;
  workLocation: GroupWorkLocation;
  manager?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members?: GroupMember[];
}

interface GroupMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    displayName?: string;
    title?: string;
  };
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName?: string;
  role?: string;
  class?: string;
  title?: string;
}


export default function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const groupsListSearch = (location.state as { groupsListSearch?: string } | null)?.groupsListSearch;
  const groupsListPath =
    groupsListSearch !== undefined ? `/groups${groupsListSearch}` : '/groups';
  const { canViewEntity, canUpdateEntity, canCreateEntity, loading: permissionsLoading } = useUserPermissions();
  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<Group>>({});
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedAvailableUsers, setSelectedAvailableUsers] = useState<string[]>([]);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState<string[]>([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchMembers, setSearchMembers] = useState('');
  const [pendingAdditions, setPendingAdditions] = useState<string[]>([]);
  const [pendingRemovals, setPendingRemovals] = useState<string[]>([]);

  // Check permissions
  const canAccessGroups = canViewEntity('groups');
  const canEditGroups = canUpdateEntity('groups');
  const canCreateGroups = canCreateEntity('groups');

  const fetchGroup = useCallback(async () => {
    try {
      const response = await api.get(`/groups/${id}`);
      setGroup(response.data);
      setFormData({
        ...response.data,
        workLocation: response.data.workLocation || DEFAULT_GROUP_WORK_LOCATION,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching group:', error);
      setLoading(false);
    }
  }, [id]);

  const fetchAvailableData = useCallback(async () => {
    try {
      try {
        // Load Tenarai users and contacts; UI filters by group type
        const usersRes = await api.get('/users?limit=1000&class=user,contact');
        const users = usersRes.data.users || usersRes.data || [];
        setAvailableUsers(users);
      } catch (err) {
        console.error('Error fetching users:', err);
        setAvailableUsers([]);
      }



    } catch (error) {
      console.error('Error in fetchAvailableData:', error);
    }
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      if (id && id !== 'new') {
        await fetchGroup();
        await fetchAvailableData();
      } else if (id === 'new') {
        setEditMode(true);
        setFormData({
          name: '',
          description: '',
          type: 'assignment',
          groupType: 'itil',
          email: '',
          isActive: true,
          timezone: 'UTC',
          escalationLevel: 1,
          autoAssign: false,
          skills: [],
          workLocation: DEFAULT_GROUP_WORK_LOCATION,
        });
        await fetchAvailableData();
        setLoading(false);
      }
    };
    
    initializePage();
  }, [id, fetchGroup, fetchAvailableData]);

  

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean up the form data before sending
      const dataToSend: any = {
        name: formData.name,
        type: formData.type || 'assignment',
        groupType: formData.groupType || 'itil',
        timezone: formData.timezone || 'UTC',
        escalationLevel: formData.escalationLevel || 1,
        autoAssign: formData.autoAssign || false,
        isActive: formData.isActive !== undefined ? formData.isActive : true,
        skills: formData.skills || [],
        workLocation: formData.workLocation || DEFAULT_GROUP_WORK_LOCATION,
      };

      // For optional fields, explicitly handle empty strings as null
      dataToSend.description = formData.description === '' ? null : formData.description;
      dataToSend.email = formData.email === '' ? null : formData.email;
      dataToSend.managerId = formData.managerId === '' ? null : formData.managerId;
      dataToSend.workSchedule = formData.workSchedule === '' ? null : formData.workSchedule;

      if (id === 'new') {
        const response = await api.post('/groups', dataToSend);
        toast.success('Group created successfully!');
        navigate(`/groups/${response.data.id}`, { state: location.state });
      } else {
        await api.put(`/groups/${id}`, dataToSend);
        await fetchGroup();
        setEditMode(false);
        toast.success('Group updated successfully!');
      }
    } catch (error: any) {
      console.error('Error saving group:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save group';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // const handleAddMember = async () => {
  //   if (!selectedUserId) return;
  //
  //   try {
  //     await api.post(`/groups/${id}/members`, {
  //       userId: selectedUserId,
  //       role: memberRole
  //     });
  //     fetchGroup();
  //     setSelectedUserId('');
  //     setMemberRole('member');
  //   } catch (error) {
  //     console.error('Error adding member:', error);
  //     toast.error('Failed to add member');
  //   }
  // };

  // const handleRemoveMember = async (userId: string) => {
  //   if (window.confirm('Are you sure you want to remove this member?')) {
  //     try {
  //       await api.delete(`/groups/${id}/members/${userId}`);
  //       fetchGroup();
  //     } catch (error) {
  //       console.error('Error removing member:', error);
  //       toast.error('Failed to remove member');
  //     }
  //   }
  // };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Check if user can access groups
  if (!canAccessGroups && id !== 'new') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Check if user can create groups for new group page
  if (id === 'new' && !canCreateGroups) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to create groups.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const tabs = [
    { name: 'Details', icon: Cog6ToothIcon },
    { name: 'Group Members', icon: UsersIcon, count: group?.members?.length || 0 }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <Link
          to={groupsListPath}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Groups
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id === 'new' ? 'New Group' : formData.name || 'Loading...'}
            </h1>
            {group && (
              <p className="text-gray-600 mt-1">{stripHtml(group.description)}</p>
            )}
          </div>
          
          <div className="flex gap-2">
            {!editMode && id !== 'new' && canEditGroups && (
              <button
                onClick={() => setEditMode(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Edit
              </button>
            )}
            {editMode && (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                {id !== 'new' && (
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFormData(group || {});
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-6">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                `w-full rounded-lg py-2.5 text-sm font-medium leading-5
                 ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
                 ${selected
                   ? 'bg-white text-blue-700 shadow'
                   : 'text-blue-600 hover:bg-white/[0.12] hover:text-blue-800'
                 }`
              }
            >
              <div className="flex items-center justify-center">
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </div>
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          {/* Details Tab */}
          <Tab.Panel className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager
                </label>
                <select
                  value={formData.managerId || ''}
                  onChange={(e) => handleInputChange('managerId', e.target.value)}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select Manager</option>
                  {Array.isArray(availableUsers) && availableUsers
                    .filter(user => (user.role as any)?.name !== 'super_admin')
                    .filter(user => user.title === 'Manager')
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                <select
                  value={formData.type || 'assignment'}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  disabled={!editMode || group?.type === 'peer_review' || group?.type === 'customer_approver' || group?.type === 'assessment'}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  {group?.type === 'peer_review' ? (
                    <option value="peer_review">Peer Review</option>
                  ) : group?.type === 'customer_approver' ? (
                    <option value="customer_approver">Can Approve on behalf of Customer</option>
                  ) : group?.type === 'assessment' ? (
                    <option value="assessment">Assessment</option>
                  ) : (
                    <>
                      <option value="assignment">Assignment</option>
                      <option value="approval">Approval</option>
                      <option value="notification">Notification</option>
                      <option value="billing">Billing</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Hourly Rate *
                </label>
                <input
                  type="number"
                  value={formData.defaultHourlyRate || 0}
                  onChange={(e) => handleInputChange('defaultHourlyRate', parseFloat(e.target.value))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Type *
                </label>
                <select
                  value={formData.groupType || 'itil'}
                  onChange={(e) => handleInputChange('groupType', e.target.value)}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="catalog">Catalog</option>
                  <option value="itil">ITIL</option>
                  <option value="schedule_visible_externally">Schedule Visible Externally</option>
                  <option value="schedule_visible_internally">Schedule Visible Internally</option>
                  <option value="survey">Survey</option>
                  <option value="service_desk">Service Desk</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <GroupWorkLocationField
                  value={formData.workLocation}
                  onChange={(workLocation) => handleInputChange('workLocation', workLocation)}
                  readOnly={!editMode}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={!editMode}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
              </div>
            </div>
          </Tab.Panel>

          {/* Group Members Tab */}
          <Tab.Panel className="bg-white rounded-lg shadow p-6">
            {id !== 'new' ? (
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Group Members List</h3>
                  <p className="text-sm text-gray-600 mt-1">{group?.name || 'Group'}</p>
                </div>
                
                <div className="grid grid-cols-11 gap-4">
                  {/* Available Users Column */}
                  <div className="col-span-5">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Collection</h4>
                    <div className="border rounded-lg bg-gray-50 p-3">
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchAvailable}
                        onChange={(e) => setSearchAvailable(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 mb-3"
                      />
                      <div className="bg-white border rounded-md overflow-y-auto" style={{ height: '400px' }}>
                        {availableUsers
                          .filter(u => {
                            // Show users who are:
                            // 1. Not current members (unless they're marked for removal)
                            // 2. Not in pending additions
                            const isCurrentMember = group?.members?.some(m => m.userId === u.id);
                            const isMarkedForRemoval = pendingRemovals.includes(u.id);
                            const isMarkedForAddition = pendingAdditions.includes(u.id);

                            return (!isCurrentMember || isMarkedForRemoval) && !isMarkedForAddition;
                          })
                          .filter(u => (u.role as any)?.name !== 'super_admin')
                          .filter(u => {
                            const groupType = group?.type || formData.type || 'assignment';
                            // Assignment groups can include Tenarai users and customer contacts
                            if (groupType === 'assignment') {
                              const userClass = u.class?.toLowerCase();
                              return userClass === 'user' || userClass === 'contact';
                            }
                            return u.class?.toLowerCase() === 'user';
                          })
                          .filter(u => {
                            const searchTerm = searchAvailable.toLowerCase();
                            return `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm) ||
                                   u.email.toLowerCase().includes(searchTerm);
                          })
                          .map(user => (
                            <div
                              key={user.id}
                              className={`px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 ${
                                selectedAvailableUsers.includes(user.id) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                if (selectedAvailableUsers.includes(user.id)) {
                                  setSelectedAvailableUsers(selectedAvailableUsers.filter(id => id !== user.id));
                                } else {
                                  setSelectedAvailableUsers([...selectedAvailableUsers, user.id]);
                                }
                              }}
                            >
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName} ({user.email})
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>

                  {/* Arrow Buttons Column */}
                  <div className="col-span-1 flex flex-col justify-center items-center space-y-4">
                    <button
                      onClick={() => {
                        if (selectedAvailableUsers.length > 0) {
                          // Add selected users to pending additions
                          const newAdditions = selectedAvailableUsers.filter(id => !pendingAdditions.includes(id));
                          setPendingAdditions([...pendingAdditions, ...newAdditions]);
                          // Remove from pending removals if they were there
                          setPendingRemovals(pendingRemovals.filter(id => !selectedAvailableUsers.includes(id)));
                          setSelectedAvailableUsers([]);
                        }
                      }}
                      disabled={selectedAvailableUsers.length === 0}
                      className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Add selected users to group"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        if (selectedGroupMembers.length > 0) {
                          // Add existing members to pending removals
                          const existingMemberIds = selectedGroupMembers.filter(id => 
                            group?.members?.some(m => m.userId === id)
                          );
                          const newRemovals = existingMemberIds.filter(id => !pendingRemovals.includes(id));
                          setPendingRemovals([...pendingRemovals, ...newRemovals]);
                          
                          // Remove from pending additions if they were there
                          setPendingAdditions(pendingAdditions.filter(id => !selectedGroupMembers.includes(id)));
                          setSelectedGroupMembers([]);
                        }
                      }}
                      disabled={selectedGroupMembers.length === 0}
                      className="p-2 border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Remove selected members from group"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Group Members Column */}
                  <div className="col-span-5">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Group Members List</h4>
                    <div className="border rounded-lg bg-gray-50 p-3">
                      <input
                        type="text"
                        placeholder="Search..."
                        value={searchMembers}
                        onChange={(e) => setSearchMembers(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 mb-3"
                      />
                      <div className="bg-white border rounded-md overflow-y-auto" style={{ height: '400px' }}>
                        {/* Existing Members (not marked for removal) */}
                        {group?.members
                          ?.filter(member => !pendingRemovals.includes(member.userId))
                          .filter(member => {
                            const searchTerm = searchMembers.toLowerCase();
                            return `${member.user.firstName} ${member.user.lastName}`.toLowerCase().includes(searchTerm) ||
                                   member.user.email.toLowerCase().includes(searchTerm);
                          })
                          .map(member => (
                            <div
                              key={member.id}
                              className={`px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 ${
                                selectedGroupMembers.includes(member.userId) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                if (selectedGroupMembers.includes(member.userId)) {
                                  setSelectedGroupMembers(selectedGroupMembers.filter(id => id !== member.userId));
                                } else {
                                  setSelectedGroupMembers([...selectedGroupMembers, member.userId]);
                                }
                              }}
                            >
                              <div className="text-sm font-medium text-gray-900">
                                {member.user.firstName} {member.user.lastName} ({member.user.email})
                              </div>
                            </div>
                          ))}
                        
                        {/* Pending Additions */}
                        {availableUsers
                          .filter(u => pendingAdditions.includes(u.id))
                          .filter(u => {
                            const groupType = group?.type || formData.type || 'assignment';
                            if (groupType === 'assignment') {
                              const userClass = u.class?.toLowerCase();
                              return userClass === 'user' || userClass === 'contact';
                            }
                            return u.class?.toLowerCase() === 'user';
                          })
                          .filter(u => {
                            const searchTerm = searchMembers.toLowerCase();
                            return `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm) ||
                                   u.email.toLowerCase().includes(searchTerm);
                          })
                          .map(user => (
                            <div
                              key={user.id}
                              className={`px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 ${
                                selectedGroupMembers.includes(user.id) ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                if (selectedGroupMembers.includes(user.id)) {
                                  setSelectedGroupMembers(selectedGroupMembers.filter(id => id !== user.id));
                                } else {
                                  setSelectedGroupMembers([...selectedGroupMembers, user.id]);
                                }
                              }}
                            >
                              <div className="text-sm font-medium text-green-600">
                                {user.firstName} {user.lastName} ({user.email}) <span className="italic">(to be added)</span>
                              </div>
                            </div>
                          ))}
                        
                        {(!group?.members || (group.members.length === 0 && pendingAdditions.length === 0) || 
                          (group.members.filter(m => !pendingRemovals.includes(m.userId)).length === 0 && pendingAdditions.length === 0)) && (
                          <div className="text-center py-8 text-gray-400 text-sm">
                            --None--
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setPendingAdditions([]);
                      setPendingRemovals([]);
                      setSelectedAvailableUsers([]);
                      setSelectedGroupMembers([]);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        // Add pending members
                        for (const userId of pendingAdditions) {
                          await api.post(`/groups/${id}/members`, {
                            userId,
                            role: 'member'
                          });
                        }
                        
                        // Remove pending removals
                        for (const userId of pendingRemovals) {
                          await api.delete(`/groups/${id}/members/${userId}`);
                        }
                        
                        // Refresh and reset
                        await fetchGroup();
                        setPendingAdditions([]);
                        setPendingRemovals([]);
                        setSelectedAvailableUsers([]);
                        setSelectedGroupMembers([]);
                        
                        // Show success notification
                        toast.success('Group members updated successfully!');
                      } catch (error) {
                        console.error('Error updating members:', error);
                        toast.error('Failed to update group members');
                      }
                    }}
                    disabled={pendingAdditions.length === 0 && pendingRemovals.length === 0}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Save the group first to add members
              </div>
            )}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}